import { toastSuccess } from '@components/Toast/Toast';
import { Chip, CopyIcon } from '@gurupay/gp-ui-library';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountStatus } from 'types/operations.ts';

interface AccountInfoProps {
	showLabel?: boolean;
	iban: string;
	status: AccountStatus;
	label?: string | null;
	ownerName: string;
	uuid: string;
}

const statusLabels: Partial<Record<AccountStatus, string>> = {
	[AccountStatus.CLOSED]: 'accounts.filters.status.closed',
	[AccountStatus.BLOCKED]: 'accounts.filters.status.blocked',
	[AccountStatus.CREDIT_BLOCKED]: 'accounts.filters.status.creditBlocked',
};

export const AccountInfo = ({
	iban,
	ownerName,
	status,
	label,
	uuid,
	showLabel = true,
}: AccountInfoProps) => {
	const { t } = useTranslation();

	const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		navigator?.clipboard?.writeText(iban);
		toastSuccess(t('common.successCopyToClipboard'));
	};

	if (!uuid) {
		return null;
	}

	return (
		<div>
			<div className="text-sm-bold text-neutral-950 flex flex-row gap-space-4 items-center">
				{iban}
				<button
					aria-label={t('common.copyToClipboard', { iban })}
					className="bg-transparent border-0 p-0"
					onClick={handleCopy}
					type="button"
				>
					<CopyIcon
						aria-hidden="true"
						className="w-space-16 h-space-16 text-neutral-600 hover:text-neutral-800 cursor-pointer transition-all duration-300 ease-in-out"
					/>
				</button>
				{showLabel && !!statusLabels[status] && (
					<Chip
						className="mt-space-4"
						color={status === 'CLOSED' ? 'error' : 'warning'}
						label={t(statusLabels[status])}
						size="small"
						variant="filled"
					/>
				)}
			</div>
			<div className="text-sm-medium text-neutral-600">
				{ownerName}
				{showLabel && label ? ` | ${label}` : ''}
			</div>
		</div>
	);
};
